import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type { TimelineGroupBy } from '@/store/TimelineDisplayStore'
import useTimelineDisplayStore from '@/store/TimelineDisplayStore'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-3">
      <span className="text-[13px] text-foreground/80">{label}</span>
      {children}
    </div>
  )
}

/**
 * Timeline-only options for the Display popover (design 27). Self-contained:
 * reads and writes its own persisted store, so it can be mounted as the
 * `timeline` entry of the Display popover's per-layout section.
 */
export function TimelineDisplaySection({ className }: { className?: string }) {
  const { t } = useTranslation('projects')
  const groupBy = useTimelineDisplayStore((s) => s.groupBy)
  const setGroupBy = useTimelineDisplayStore((s) => s.setGroupBy)
  const showCycles = useTimelineDisplayStore((s) => s.showCycles)
  const setShowCycles = useTimelineDisplayStore((s) => s.setShowCycles)
  const showDependencies = useTimelineDisplayStore((s) => s.showDependencies)
  const setShowDependencies = useTimelineDisplayStore((s) => s.setShowDependencies)
  const showUndated = useTimelineDisplayStore((s) => s.showUndated)
  const setShowUndated = useTimelineDisplayStore((s) => s.setShowUndated)

  const groupOptions: Array<{ value: TimelineGroupBy; label: string }> = [
    { value: 'status', label: t('timeline.display.groupStatus') },
    { value: 'none', label: t('timeline.display.groupNone') },
  ]

  return (
    <div className={cn('space-y-1.5 px-4 py-3', className)} data-testid="timeline-display-section">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {t('timeline.display.title')}
      </p>
      <Row label={t('timeline.display.groupBy')}>
        <div role="radiogroup" aria-label={t('timeline.display.groupBy')} className="flex rounded-lg bg-secondary p-0.5">
          {groupOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={groupBy === opt.value}
              onClick={() => setGroupBy(opt.value)}
              className={cn(
                'h-6 rounded-md px-2.5 text-[12px] transition-colors',
                groupBy === opt.value
                  ? 'bg-card font-medium text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Row>
      <Row label={t('timeline.display.cycles')}>
        <Switch checked={showCycles} onCheckedChange={setShowCycles} aria-label={t('timeline.display.cycles')} />
      </Row>
      <Row label={t('timeline.display.dependencies')}>
        <Switch
          checked={showDependencies}
          onCheckedChange={setShowDependencies}
          aria-label={t('timeline.display.dependencies')}
        />
      </Row>
      <Row label={t('timeline.display.undated')}>
        <Switch checked={showUndated} onCheckedChange={setShowUndated} aria-label={t('timeline.display.undated')} />
      </Row>
    </div>
  )
}
